import {
  Core_AddressMsg,
  Core_ContactMsg,
  Core_LocalDateMsg,
  Core_LocalTimeMsg,
  Input_Core_PhoneNumberMsg,
  Patientportal_PortalFacilityMsg,
  Patientportal_PortalUserMsg,
  Resource_AppointmentLocation,
  Resource_AppointmentMsg,
  Resource_AppointmentStatus,
  Resource_AppointmentTypeMsg,
  TimeUnit,
  useFindCurrentPortalUserQuery,
} from '@/generated/graphql';
import { HomeOutlined, PhoneOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { ApolloError } from '@apollo/client';
import { Maybe } from 'graphql/jsutils/Maybe';
import moment, { Moment } from 'moment';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { defaultSettings } from '../defaultSettings';
import { buildAddress } from './formatters';
import styles from './utils.module.scss';

interface CurrentUser {
  userLoading?: boolean;
  error?: ApolloError;
  currentUser: Patientportal_PortalUserMsg | null | undefined;
  refetch: () => void;
}

// Extracts the primary address or falls back to the first address in the array
export const extractPrimaryAddress = (
  contact: Nullable<Core_ContactMsg>,
): Nullable<Core_AddressMsg> => {
  return contact?.addresses?.find((address) => address?.primary) ?? contact?.addresses?.[0];
};

export const momentToLocalDate = (moment: Moment | null): Maybe<Core_LocalDateMsg> | undefined => {
  if (!!moment) {
    return {
      day: moment.date(),
      month: moment.month() + 1,
      year: moment.year(),
    };
  } else {
    return {
      day: null,
      month: null,
      year: null,
    };
  }
};

export const momentToLocalTime = (moment: Moment | null): Maybe<Core_LocalTimeMsg> | undefined => {
  if (!!moment) {
    return {
      hour: moment.hour(),
      minute: moment.minute(),
      second: moment.second(),
    };
  } else {
    return {
      hour: null,
      minute: null,
      second: null,
    };
  }
};

export const localTimeToMoment = (
  localTime: Maybe<Core_LocalTimeMsg> | undefined,
): Moment | null => {
  if (
    !localTime ||
    localTime.hour === null ||
    localTime.hour === undefined ||
    localTime.minute === undefined ||
    localTime.minute === null ||
    localTime.second === undefined ||
    localTime.second === null
  ) {
    return null;
  }

  return moment().set({
    hour: localTime.hour,
    minute: localTime.minute,
    second: localTime.second,
  });
};

export const localDateToMoment = (
  localDate: Maybe<Core_LocalDateMsg> | undefined,
): Moment | null => {
  if (
    !localDate ||
    localDate.day === null ||
    localDate.day === undefined ||
    localDate.month === null ||
    localDate.month === undefined ||
    localDate.year === null ||
    localDate.year === undefined
  ) {
    return null;
  }

  return moment()
    .set({
      year: localDate.year,
      month: localDate.month + 1,
      date: localDate.day,
    })
    .startOf('day');
};

interface AppointmentLocationProps {
  location: Maybe<Resource_AppointmentLocation> | undefined;
}
export const AppointmentLocation: FC<AppointmentLocationProps> = ({ location }) => {
  const { formatMessage } = useIntl();
  if (location === Resource_AppointmentLocation.AppointmentLocationVideo) {
    return (
      <div className={styles.appointmentLocation}>
        <span style={{ marginRight: 15 }}>
          <VideoCameraOutlined /> {formatMessage({ id: 'video-appointment' })}
        </span>
      </div>
    );
  } else if (location === Resource_AppointmentLocation.AppointmentLocationPhone) {
    return (
      <div className={styles.appointmentLocation}>
        <span style={{ marginRight: 15 }}>
          <PhoneOutlined /> {formatMessage({ id: 'phone-appointment' })}
        </span>
      </div>
    );
  }
  {
    return (
      <div className={styles.appointmentLocation}>
        <span style={{ marginRight: 15 }}>
          <HomeOutlined /> {formatMessage({ id: 'in-person-appointment' })}
        </span>
      </div>
    );
  }
};

export const renderMarkdown = (
  text: Maybe<string | React.ReactNode> | undefined,
): React.ReactNode => {
  if (!text) {
    return null;
  }

  return (
    <ReactMarkdown remarkPlugins={[]} rehypePlugins={[rehypeRaw]}>
      {/*@ts-ignore*/}
      {text}
    </ReactMarkdown>
  );
};

export function isValidEmail(email: string) {
  return /\S+@\S+\.\S+/.test(email);
}

export function arrayCompare<T>(_arr1: T[], _arr2: T[]) {
  if (!Array.isArray(_arr1) || !Array.isArray(_arr2) || _arr1.length !== _arr2.length) {
    return false;
  }

  // .concat() to not mutate arguments
  const arr1 = _arr1.concat().sort();
  const arr2 = _arr2.concat().sort();

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

export const highlightTitle = (title: string, searchQuery: string | undefined): React.ReactNode => {
  const strTitle = title;
  if (!searchQuery) {
    return title;
  }
  const index = strTitle.toLocaleLowerCase().indexOf(searchQuery.toLowerCase());
  const beforeStr = strTitle.substring(0, index);
  const afterStr = strTitle.slice(index + searchQuery.length);
  const betweenStr = strTitle.slice(index, index + searchQuery.length);
  return index > -1 ? (
    <span className={styles.searchText}>
      {beforeStr}
      <span className={styles.searchHighlight}>{betweenStr}</span>
      {afterStr}
    </span>
  ) : (
    <span>{strTitle}</span>
  );
};

export function useCurrentUser(): CurrentUser {
  const { data, loading, error, refetch } = useFindCurrentPortalUserQuery({
    fetchPolicy: 'network-only',
  });
  return {
    currentUser: data?.findCurrentPortalUser?.portalUser,
    userLoading: loading,
    error,
    refetch,
  };
}

export type PhoneNumber = {
  number?: number | undefined;
  countryCode?: number | undefined;
};

export function validatePhoneNumber(phoneNumber: string) {
  return /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/i.test(phoneNumber);
}

export function formatAccountPhoneNumber({
  phoneNumber,
  countryCode,
}: {
  phoneNumber: Nullable<string>;
  countryCode: Nullable<number>;
}): string | undefined {
  if (phoneNumber && countryCode) {
    return `+${countryCode} ${phoneNumber}`;
  }

  return undefined;
}

export function formatPhoneNumber(phoneNumber?: Maybe<Input_Core_PhoneNumberMsg>) {
  if (!phoneNumber) {
    return '-';
  }

  const preDial = phoneNumber?.countryCode ? `+${phoneNumber?.countryCode}` : '';

  return `${preDial || '+49'} ${phoneNumber.number || '-'}`;
}

export function buildAddressString(address?: Maybe<Core_AddressMsg>): string | undefined {
  if (!address) {
    return undefined;
  }

  const addressStringShort = buildAddressStringShort(address);
  const addressAdditionString = buildAddressAdditionString(address);

  if (addressStringShort && addressAdditionString) {
    return `${addressStringShort}, ${addressAdditionString}`;
  }
  if (!addressStringShort) {
    return addressAdditionString;
  }
  if (!addressAdditionString) {
    return addressStringShort;
  }
  return undefined;
}

export function buildAddressAdditionString(address?: Maybe<Core_AddressMsg>): string | undefined {
  if (!address || !address?.city || !address.zipCode) {
    return undefined;
  }

  return `${address.zipCode} ${address.city}`;
}

export function buildAddressStringShort(address?: Maybe<Core_AddressMsg>): string | undefined {
  if (!address || !address?.street || !address.houseNumber) {
    return undefined;
  }

  return `${address.street} ${address.houseNumber}`;
}

export interface ICalEvent {
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  attendees?: string[];
}

export function appointmentToICalEvent(
  appointment: Maybe<Resource_AppointmentMsg> | undefined,
  portalFacilities: Maybe<Maybe<Patientportal_PortalFacilityMsg>[]> | undefined,
  appointmentType?: Maybe<string> | undefined,
): ICalEvent {
  const ou = portalFacilities?.[portalFacilities?.length - 1];

  let timezoneOffset = new Date().getTimezoneOffset() / -60;
  const GERMAN_TIMEZONE = 2;
  const diff = timezoneOffset - GERMAN_TIMEZONE;

  const startTime = moment(appointment?.startTime?.seconds * 1000)
    .add(diff, 'hour')
    .toISOString();
  const endTime = moment(appointment?.endTime?.seconds * 1000)
    .add(diff, 'hour')
    .toISOString();

  const path = portalFacilities?.map((facility) => facility!.name!).join(' / ');

  return {
    title: ou?.name + ' | ' + appointmentType,
    description: path,
    location: buildAddress(extractPrimaryAddress(ou?.contact)),
    startTime,
    endTime,
  };
}

export const buildImageDownloadUrl = (imageId: string): string =>
  `${defaultSettings.imageDownload}/${imageId}`;

export function getAttendeeStatus(
  appointment: Maybe<Resource_AppointmentMsg>,
  portalUserId: Maybe<string>,
): Maybe<Resource_AppointmentStatus> {
  const attendees = appointment?.attendees;

  if (!attendees?.length) {
    return appointment?.appointmentStatus;
  }

  const matchingAttendee = appointment?.attendees?.find(
    (attendee) => attendee?.portalUserId === portalUserId,
  );

  return matchingAttendee?.attendeeStatus ?? appointment?.appointmentStatus;
}

export function appointmentToGoogleEvent(
  appointment: Maybe<Resource_AppointmentMsg> | undefined,
  portalFacilities: Maybe<Maybe<Patientportal_PortalFacilityMsg>[]> | undefined,
  appointmentType?: Maybe<string> | undefined,
) {
  const ou = portalFacilities?.[portalFacilities?.length - 1];

  let timezoneOffset = new Date().getTimezoneOffset() / -60;
  const GERMAN_TIMEZONE = 2;
  const diff = timezoneOffset - GERMAN_TIMEZONE;

  const startTime = moment(new Date(appointment?.startTime?.seconds * 1000))
    .add(diff, 'hour')
    .toISOString();
  const endTime = moment(appointment?.endTime?.seconds * 1000)
    .add(diff, 'hour')
    .toISOString();

  const path = portalFacilities?.map((facility) => facility!.name!).join(' / ');
  return {
    summary: ou?.name + ' | ' + appointmentType,
    description: path,
    location: buildAddress(extractPrimaryAddress(ou?.contact)),
    start: {
      dateTime: startTime,
      timeZone: 'Europe/Berlin',
    },
    end: {
      dateTime: endTime,
      timeZone: 'Europe/Berlin',
    },
    reminders: {
      useDefault: false,
      overrides: [
        { method: 'email', minutes: 24 * 60 },
        { method: 'popup', minutes: 10 },
      ],
    },
  };
}

export function getAppointmentTypeName(appointmentType: Maybe<Resource_AppointmentTypeMsg>) {
  if (!!appointmentType?.portalDisplayName) {
    return appointmentType?.portalDisplayName;
  }
  return appointmentType?.name;
}

export function getOfficeAddress() {
  return (
    <p>
      Ridlerstraße 39
      <br />
      80339 München
      <br />
      Deutschland
    </p>
  );
}

export function timeUnitToSeconds(timeUnit: TimeUnit) {
  switch (timeUnit) {
    case TimeUnit.TimeUnitHour:
      return 60 * 60;
    case TimeUnit.TimeUnitDay:
      return 60 * 60 * 24;
    case TimeUnit.TimeUnitMinute:
      return 60;
    default:
      return 60 * 60;
  }
}

export function timeUnitToGerman(timeUnit: TimeUnit) {
  switch (timeUnit) {
    case TimeUnit.TimeUnitHour:
      return 'Stunden';
    case TimeUnit.TimeUnitDay:
      return 'Tage';
    case TimeUnit.TimeUnitMinute:
      return 'Minuten';
    default:
      return 'Stunden';
  }
}
